import * as React from 'react';
import { useDiagnosisPermission } from '../../../contexts/DiagnosisPermissionContext';
import { forwardRef, useState, useMemo, useEffect } from 'react';
import {
  Stack,
  Typography,
  Box,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import toast from 'react-hot-toast';
import { useQuery, useMutation, useQueryClient, useQueries } from 'react-query';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LineChartWrapper } from '../../charts';

import { ReadOnlyDataGrid, ReadOnlyField, RowAction } from '../../ReadOnlyDataGrid';
import {
  CrossEntityDateCrudDataGrid,
  BaseEntity,
  BaseData,
  ValueWithEntityReference,
  CrossEntityDateCrudDataGridHandle,
  CrossEntityTableDataItem,
} from '../../CrossEntityDateCrudDataGrid';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import {
  PodTimetablesReadDTO,
  PodTimetablesDataReadDTO,
  PodTimetablesDataCreateDTO,
  DiagnosisReadDTO,
  DiagnosisEnergyVectorsReadDTO,
  DiagnosisCustomerSitesReadDTO,
  WeeklySeriesDTO,
  SeasonEnum,
  IPodTimetablesReadDTO,
  DiagnosisEnergyUseReadDTO,
} from '../../../generated/api-client';
import { DiagnosisStepHandle } from './Step.types';
import { apiClient, axiosInstance } from '../../../services/apiClient';
import { getAxiosErrorDetail } from '../../../utils/axiosErrorDetail';
import { dateTimeToLocalApiString } from '../../../utils/date.utils';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import { ActionSpacer } from '../../common';
import {
  MissingDataDialog,
  MissingDateRange,
  groupMissingDates,
} from '../../common/MissingDataDialog';
import { DECIMAL_PLACES } from '../../../constants/decimalPlacesPolicy';
import { useBatchSavePodTimetablesData } from '../../../hooks/usePodTimetablesData';
import { GridRowId } from '@mui/x-data-grid';
import { EditPodTimetableDialog } from './EditPodTimetableDialog';
import { useDiagnosisCustomerSitesById, useDiagnosisEnergyUseByDiagnosisId } from '../../../hooks';
import { getFunctionalAreaLabel } from '../../../utils/functionalArea';
import { formatNumberItalian } from '../../../utils/numberFormat';

import 'dayjs/locale/it';

dayjs.extend(weekday);

dayjs.locale('it');

interface DiagnosisPodTimetablesStepProps {
  diagnosisId?: string;
  diagnosis?: DiagnosisReadDTO | null;
}

type ViewMode = 'table' | 'charts';
type SeasonKey = 'all' | 'summer' | 'winter';

interface WeeklyProfilesBySeason {
  all: WeeklySeriesDTO[];
  summer: WeeklySeriesDTO[];
  winter: WeeklySeriesDTO[];
}

const CHART_SEASONS: Array<{
  key: SeasonKey;
  label: string;
  icon: React.ReactNode;
  enumValue: SeasonEnum;
}> = [
  {
    key: 'all',
    label: 'Totale',
    icon: <ShowChartIcon fontSize="small" />,
    enumValue: SeasonEnum.All,
  },
  {
    key: 'summer',
    label: 'Estate',
    icon: <WbSunnyIcon fontSize="small" />,
    enumValue: SeasonEnum.Summer,
  },
  {
    key: 'winter',
    label: 'Inverno',
    icon: <AcUnitIcon fontSize="small" />,
    enumValue: SeasonEnum.Winter,
  },
];

const WEEKDAY_COLORS = [
  '#FF6B6B',
  '#FFA600',
  '#FFD166',
  '#06D6A0',
  '#118AB2',
  '#5E60CE',
  '#8338EC',
];
const WEEKDAY_IT_LABELS: Record<string, string> = {
  monday: 'Lunedì',
  tuesday: 'Martedì',
  wednesday: 'Mercoledì',
  thursday: 'Giovedì',
  friday: 'Venerdì',
  saturday: 'Sabato',
  sunday: 'Domenica',
};

// Funzione per determinare se una data è in estate o inverno
const getSeason = (
  date: Date,
  startSummer: string | null | undefined,
  startWinter: string | null | undefined,
  year: number
): 'summer' | 'winter' => {
  if (!startSummer || !startWinter) {
    // Default: estate da aprile a settembre
    const month = date.getMonth() + 1;
    return month >= 4 && month <= 9 ? 'summer' : 'winter';
  }

  // Parse startSummer e startWinter (formato DD/MM)
  const [summerDay, summerMonth] = startSummer.split('/').map(Number);
  const [winterDay, winterMonth] = startWinter.split('/').map(Number);

  const summerDate = dayjs(`${year}-${summerMonth}-${summerDay}`);
  const winterDate = dayjs(`${year}-${winterMonth}-${winterDay}`);
  const currentDate = dayjs(date);

  // Se startSummer è prima di startWinter (es. estate inizia a marzo, inverno a dicembre)
  if (summerDate.isBefore(winterDate)) {
    // Estate: da startSummer a startWinter (escluso)
    if (
      (currentDate.isAfter(summerDate) || currentDate.isSame(summerDate)) &&
      currentDate.isBefore(winterDate)
    ) {
      return 'summer';
    }
    return 'winter';
  } else {
    // Se startSummer è dopo startWinter (es. estate inizia a giugno, inverno a dicembre dell'anno precedente)
    // Estate: da startSummer alla fine dell'anno o da inizio anno a startWinter
    if (
      currentDate.isAfter(summerDate) ||
      currentDate.isSame(summerDate) ||
      currentDate.isBefore(winterDate)
    ) {
      return 'summer';
    }
    return 'winter';
  }
};

const getPodLabel = (
  pod: PodTimetablesReadDTO,
  energyUses: DiagnosisEnergyUseReadDTO[] = []
): string => {
  const energyUseId = (pod as any).energy_use_id;
  const energyUse = energyUseId ? energyUses.find(eu => eu.id === energyUseId) : null;
  return pod.code || pod.chart || pod.hierarchy || energyUse?.code || pod.id || 'POD';
};
const getItalianWeekdayLabel = (weekday: string): string =>
  WEEKDAY_IT_LABELS[weekday.toLowerCase()] || weekday;

export const DiagnosisPodTimetablesStep = React.memo(
  forwardRef<DiagnosisStepHandle, DiagnosisPodTimetablesStepProps>(
    ({ diagnosisId, diagnosis }, ref) => {
      const { readOnly } = useDiagnosisPermission();
      const theme = useTheme();
      const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
      const queryClient = useQueryClient();
      const crossEntityGridRef = React.useRef<CrossEntityDateCrudDataGridHandle>(null);
      const [viewMode, setViewMode] = useState<ViewMode>('table');
      const [editDialogOpen, setEditDialogOpen] = useState(false);
      const [selectedPodTimetable, setSelectedPodTimetable] = useState<PodTimetablesReadDTO | null>(
        null
      );
      const [deleteDialogState, setDeleteDialogState] = useState<{
        open: boolean;
        id: string | null;
      }>({ open: false, id: null });

      const [missingDataDialogState, setMissingDataDialogState] = useState<{
        open: boolean;
        missingData: Array<{ entityLabel: string; missingDates: MissingDateRange[] }>;
        tableData: CrossEntityTableDataItem[];
      }>({ open: false, missingData: [], tableData: [] });

      // Carica i POD timetables per la diagnosi
      const { data: podTimetables = [], isLoading } = useQuery<PodTimetablesReadDTO[]>(
        `pod-timetables-${diagnosisId}`,
        () =>
          apiClient.search_by_diagnosis_id_pod_timetables_search_by_diagnosis_id__diagnosis_id__get(
            diagnosisId!
          ),
        { enabled: !!diagnosisId }
      );

      // Carica gli usi energetici della diagnosi
      const { data: energyUses = [] } = useDiagnosisEnergyUseByDiagnosisId(diagnosisId);

      // Carica il sito della diagnosi per ottenere startSummer e startWinter
      const { data: diagnosisSite } = useDiagnosisCustomerSitesById(diagnosis?.site_id ?? '');

      const podIds = React.useMemo(
        () => podTimetables.map(p => p.id).filter(Boolean) as string[],
        [podTimetables]
      );
      const podIdsKey = podIds.join('|');

      // Carica tutti i PodTimetablesData per tutti i POD della diagnosi
      const { data: allPodTimetablesData = [] } = useQuery<PodTimetablesDataReadDTO[]>(
        [`all-pod-timetables-data-${diagnosisId}`, podIdsKey],
        async () => {
          if (!podTimetables.length) return [];
          const allData: PodTimetablesDataReadDTO[] = [];

          for (const pod of podTimetables) {
            if (pod.id) {
              const data =
                await apiClient.search_by_pod_timetable_id_pod_timetables_data_search_by_pod_timetable_id__pod_timetable_id__get(
                  pod.id
                );
              allData.push(...data);
            }
          }
          return allData;
        },
        { enabled: !!diagnosisId && podTimetables.length > 0 }
      );

      // Carica i dati totali aggregati solo se ci sono più POD
      const shouldLoadTotal = podTimetables.length > 1;
      const { data: totalPodTimetablesData = [] } = useQuery<PodTimetablesDataReadDTO[]>(
        [`total-pod-timetables-data-${diagnosisId}`],
        async () => {
          if (!diagnosisId) return [];
          try {
            const { data } = await axiosInstance.get(
              `/pod-timetables-data/total_by_diagnosis/${diagnosisId}`
            );
            // Converti i dati in PodTimetablesDataReadDTO
            return (data ?? []).map((item: any) => ({
            id: item.id,
            pod_timetable_id: item.pod_timetable_id,
            datetime: item.datetime,
            value: item.value,
            })) as PodTimetablesDataReadDTO[];
          } catch (e: unknown) {
            throw new Error(
              getAxiosErrorDetail(e, 'Failed to fetch total POD data')
            );
          }
        },
        { enabled: !!diagnosisId && shouldLoadTotal }
      );

      const handleViewModeChange = (
        _: React.MouseEvent<HTMLElement>,
        nextMode: ViewMode | null
      ) => {
        if (nextMode) {
          setViewMode(nextMode);
        }
      };

      const weeklyProfileQueriesConfig = React.useMemo(
        () =>
          podTimetables.map(pod => ({
            queryKey: ['pod-weekly-profile', pod.id],
            queryFn: async (): Promise<WeeklyProfilesBySeason | null> => {
              if (!pod.id) {
                return null;
              }
              const [total, summer, winter] = await Promise.all(
                CHART_SEASONS.map(seasonConfig =>
                  apiClient.get_weekly_profile_pod_timetables_data__pod_timetable_id__weekly_profile_get(
                    pod.id!,
                    seasonConfig.enumValue
                  )
                )
              );
              return {
                all: total?.series ?? [],
                summer: summer?.series ?? [],
                winter: winter?.series ?? [],
              };
            },
            enabled: viewMode === 'charts' && Boolean(diagnosisId) && Boolean(pod.id),
            staleTime: 1000 * 60 * 5,
          })),
        [podTimetables, viewMode, diagnosisId]
      );

      const weeklyProfilesQueries = useQueries(weeklyProfileQueriesConfig);
      const weeklyProfilesDataKey = weeklyProfilesQueries
        .map(result => JSON.stringify(result.data ?? null))
        .join('|');

      const weeklyProfilesMap = React.useMemo(() => {
        const map = new Map<string, WeeklyProfilesBySeason>();
        weeklyProfilesQueries.forEach((queryResult, index) => {
          const podId = podTimetables[index]?.id;
          if (podId && queryResult.data) {
            map.set(podId, queryResult.data);
          }
        });
        return map;
      }, [podTimetables, weeklyProfilesDataKey]);

      const chartsLoading =
        viewMode === 'charts' && weeklyProfilesQueries.some(query => query.isLoading);
      const chartsFetching =
        viewMode === 'charts' && weeklyProfilesQueries.some(query => query.isFetching);

      // Query per i grafici del totale (solo se ci sono più POD)
      const totalWeeklyProfileQuery = useQuery<WeeklyProfilesBySeason>(
        ['pod-weekly-profile-total', diagnosisId],
        async (): Promise<WeeklyProfilesBySeason> => {
          if (!diagnosisId) {
            return { all: [], summer: [], winter: [] };
          }
          const [total, summer, winter] = await Promise.all(
            CHART_SEASONS.map(async seasonConfig => {
              try {
                const { data } = await axiosInstance.get(
                  `/pod-timetables-data/diagnosis/${diagnosisId}/weekly-profile`,
                  { params: { season: seasonConfig.enumValue } }
                );
                return data;
              } catch {
                return { series: [] };
              }
            })
          );
          return {
            all: total?.series ?? [],
            summer: summer?.series ?? [],
            winter: winter?.series ?? [],
          };
        },
        { enabled: !!diagnosisId && shouldLoadTotal && viewMode === 'charts' }
      );

      const totalWeeklyProfiles = totalWeeklyProfileQuery.data;

      // Batch save mutation for pod timetables data
      const batchSavePodTimetablesData = useBatchSavePodTimetablesData();

      // Mutation for updating POD timetables order
      const updateOrderMutation = useMutation(
        async (podTimetableIds: string[]) => {
          try {
            const { data } = await axiosInstance.post('/pod-timetables/update_order', {
              pod_timetable_ids: podTimetableIds,
            });
            return data;
          } catch (e: unknown) {
            throw new Error(
              getAxiosErrorDetail(e, "Errore nel salvataggio dell'ordinamento")
            );
          }
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(`pod-timetables-${diagnosisId}`);
            toast.success('Ordinamento POD orari salvato con successo');
          },
          onError: (error: any) => {
            toast.error(error?.message || "Errore nel salvataggio dell'ordinamento dei POD orari");
          },
        }
      );

      // Periodo di riferimento: da reference_period_from 00:00 a reference_period_to 23:00 (POD orari seguono il periodo)
      const period = React.useMemo(() => {
        if (diagnosis?.reference_period_from && diagnosis?.reference_period_to) {
          const fromDate = new Date(diagnosis.reference_period_from);
          const toDate = new Date(diagnosis.reference_period_to);
          const from = new Date(
            fromDate.getFullYear(),
            fromDate.getMonth(),
            fromDate.getDate(),
            0,
            0,
            0
          );
          const to = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate(), 23, 0, 0);
          return { from, to };
        }
        if (!diagnosis?.diagnosis_date) {
          return { from: null, to: null };
        }
        const diagnosisDate = new Date(diagnosis.diagnosis_date);
        const year = diagnosisDate.getFullYear() - 1;
        const from = new Date(year, 0, 1, 0, 0, 0);
        const to = new Date(year, 11, 31, 23, 0, 0);
        return { from, to };
      }, [
        diagnosis?.reference_period_from,
        diagnosis?.reference_period_to,
        diagnosis?.diagnosis_date,
      ]);

      const batchSaveMutation = useMutation(
        async (tableData: CrossEntityTableDataItem[]) => {
          // Create a map of original values for comparison
          const originalValuesMap = new Map<string, number | null | undefined>();
          allPodTimetablesData.forEach(data => {
            if (data.pod_timetable_id && data.datetime) {
              const date = new Date(data.datetime);
              // Rimuovi timezone e normalizza
              const year = date.getFullYear();
              const month = date.getMonth() + 1;
              const day = date.getDate();
              const hour = date.getHours();
              const key = `${data.pod_timetable_id}-${year}-${month}-${day}-${hour}`;
              originalValuesMap.set(key, data.value ?? (null as any));
            }
          });

          // Raccogli solo i dati modificati (escludi i totali)
          const itemsToSave: Array<PodTimetablesDataCreateDTO & { id?: string }> = [];

          for (const item of tableData) {
            // Salta i dati totali (non devono essere salvati)
            if (item.entityId === '__TOTAL__') {
              continue;
            }
            // Normalize new value: convert to number or null
            const newValue =
              item.value !== null && item.value !== '' && item.value !== undefined
                ? Number(item.value)
                : null;

            // Crea datetime senza timezone
            const date = new Date(item.month);
            const year = date.getFullYear();
            const month = date.getMonth() + 1;
            const day = date.getDate();
            const hour = date.getHours();
            const key = `${item.entityId}-${year}-${month}-${day}-${hour}`;
            const originalValue = originalValuesMap.get(key);

            // Normalize original value for comparison
            const normalizedOriginal =
              originalValue !== null && originalValue !== undefined ? Number(originalValue) : null;

            // Skip update if value hasn't changed
            if (normalizedOriginal === newValue && item.valueId) {
              continue;
            }

            // Crea datetime senza timezone (YYYY-MM-DD HH:mm:ss)
            const datetime = new Date(year, month - 1, day, hour, 0, 0);

            const createDto = new PodTimetablesDataCreateDTO({
              pod_timetable_id: item.entityId,
              datetime: dateTimeToLocalApiString(datetime),
              value: newValue as any,
            });

            const dtoWithId = createDto as PodTimetablesDataCreateDTO & { id?: string };
            if (item.valueId) {
              dtoWithId.id = item.valueId;
            }

            itemsToSave.push(dtoWithId);
          }

          // Salva tutti i dati modificati in una sola chiamata batch
          if (itemsToSave.length > 0) {
            return await batchSavePodTimetablesData.mutateAsync(itemsToSave);
          }
        },
        {
          onSuccess: result => {
            if (!result) {
              toast.success('Nessuna modifica da salvare');
              return;
            }
            queryClient.invalidateQueries(`all-pod-timetables-data-${diagnosisId}`);
            queryClient.invalidateQueries(`total-pod-timetables-data-${diagnosisId}`);
            // Invalida anche le query dei profili settimanali per i grafici
            queryClient.invalidateQueries({
              predicate: query => {
                const key = query.queryKey;
                return Array.isArray(key) && key.length > 0 && key[0] === 'pod-weekly-profile';
              },
            });
            toast.success('Dati POD orari salvati con successo');
          },
          onError: (error: any) => {
            toast.error(error?.message || 'Errore nel salvataggio dei dati POD orari');
          },
        }
      );

      const deleteMutation = useMutation(
        async (id: string) => {
          await apiClient.delete_item_pod_timetables__item_id__delete(id);
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(`pod-timetables-${diagnosisId}`);
            queryClient.invalidateQueries(`all-pod-timetables-data-${diagnosisId}`);
            queryClient.invalidateQueries(`total-pod-timetables-data-${diagnosisId}`);
            // Invalida anche le query dei profili settimanali per i grafici
            queryClient.invalidateQueries({
              predicate: query => {
                const key = query.queryKey;
                return Array.isArray(key) && key.length > 0 && key[0] === 'pod-weekly-profile';
              },
            });
            toast.success('POD orario eliminato con successo');
            setDeleteDialogState({ open: false, id: null });
          },
          onError: (error: any) => {
            toast.error(error?.message || 'Eliminazione del POD orario non riuscita');
          },
        }
      );

      React.useImperativeHandle(ref, () => ({
        save: async () => {
          if (!crossEntityGridRef.current || !period.from || !period.to) {
            return { success: true };
          }

          // Save order if changed (compare with original order, excluding "Totale")
          const currentOrder = crossEntityGridRef.current.getEntityOrder();
          const originalOrder = podTimetables.map(p => p.id!).filter(Boolean);
          const orderChanged =
            currentOrder.length === originalOrder.length &&
            currentOrder.some((id, index) => id !== originalOrder[index]);

          if (orderChanged) {
            try {
              await updateOrderMutation.mutateAsync(currentOrder);
            } catch (error) {
              // Error handled by mutation's onError
              return { success: false };
            }
          }

          // Get all table data
          const tableData = crossEntityGridRef.current.getTableData();

          if (tableData.length === 0) {
            return { success: true };
          }

          // Verifica date mancanti per ogni POD
          const podIds = podTimetables
            .filter(pod => pod.id && pod.id !== '__TOTAL__')
            .map(pod => pod.id!);

          // Genera tutte le date/ore del periodo
          const allDates: Date[] = [];
          const currentDate = new Date(period.from);
          const endDate = new Date(period.to);

          while (currentDate <= endDate) {
            allDates.push(new Date(currentDate));
            currentDate.setHours(currentDate.getHours() + 1);
          }

          // Crea una mappa dei dati presenti per ogni POD
          const dataMap = new Map<string, Set<string>>();
          podIds.forEach(podId => {
            dataMap.set(podId, new Set());
          });

          // Popola la mappa con i dati presenti (solo se il valore non è null)
          tableData.forEach(item => {
            if (
              item.entityId &&
              item.entityId !== '__TOTAL__' &&
              item.month &&
              item.value !== null &&
              item.value !== undefined &&
              item.value !== ''
            ) {
              const date = new Date(item.month);
              const key = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}-${date.getHours()}`;
              const existingSet = dataMap.get(item.entityId);
              if (existingSet) {
                existingSet.add(key);
              }
            }
          });

          // Aggiungi anche i dati già salvati nel database (solo se il valore non è null)
          allPodTimetablesData.forEach(data => {
            if (
              data.pod_timetable_id &&
              data.datetime &&
              data.pod_timetable_id !== '__TOTAL__' &&
              data.value !== null &&
              data.value !== undefined
            ) {
              const date = new Date(data.datetime);
              const key = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}-${date.getHours()}`;
              const existingSet = dataMap.get(data.pod_timetable_id);
              if (existingSet) {
                existingSet.add(key);
              }
            }
          });

          // Trova le date mancanti per ogni POD
          const allMissingData: Array<{ entityLabel: string; missingDates: MissingDateRange[] }> =
            [];

          for (const podId of podIds) {
            const pod = podTimetables.find(p => p.id === podId);
            const podLabel = pod ? getPodLabel(pod, energyUses) : podId;
            const existingData = dataMap.get(podId) || new Set();

            const missingDates: Date[] = [];
            allDates.forEach(date => {
              const key = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}-${date.getHours()}`;
              if (!existingData.has(key)) {
                missingDates.push(new Date(date));
              }
            });

            if (missingDates.length > 0) {
              const missingRanges = groupMissingDates(missingDates);
              allMissingData.push({
                entityLabel: podLabel,
                missingDates: missingRanges,
              });
            }
          }

          // Se ci sono date mancanti, mostra il dialog
          if (allMissingData.length > 0) {
            setMissingDataDialogState({
              open: true,
              missingData: allMissingData,
              tableData: tableData,
            });
            return { success: false };
          }

          // Se non ci sono date mancanti, procedi con il salvataggio
          // Perform batch upsert
          await batchSaveMutation.mutateAsync(tableData);
          return { success: true };
        },
        add: async () => {
          if (!diagnosisId) {
            toast.error('Salva prima i dati principali della diagnosi.');
            return;
          }
          setSelectedPodTimetable(null);
          setEditDialogOpen(true);
        },
        actions: ['save', 'add'],
      }));

      const handleDeleteClick = (id: GridRowId) => {
        setDeleteDialogState({ open: true, id: id as string });
      };

      const handleCloseDeleteDialog = () => {
        setDeleteDialogState({ open: false, id: null });
      };

      const handleConfirmDelete = () => {
        if (deleteDialogState.id) {
          deleteMutation.mutate(deleteDialogState.id);
        }
      };

      const handleEditClick = (pod: PodTimetablesReadDTO) => {
        setSelectedPodTimetable(pod);
        setEditDialogOpen(true);
      };

      const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setSelectedPodTimetable(null);
      };

      // Crea entità fittizia "Totale" se ci sono più POD
      const totalEntity = React.useMemo(() => {
        if (podTimetables.length <= 1) return null;
        return {
          id: '__TOTAL__',
          code: 'Totale',
          table_code_index: 'TOTALE',
        } as IPodTimetablesReadDTO;
      }, [podTimetables.length]);

      // Prepara le entities includendo "Totale" se necessario
      const entitiesWithTotal = React.useMemo(() => {
        if (totalEntity) {
          return [...podTimetables, totalEntity];
        }
        return podTimetables;
      }, [podTimetables, totalEntity]);

      // Prepara i dati per la tabella cross-entity
      const crossEntityValues: ValueWithEntityReference<PodTimetablesDataReadDTO>[] =
        React.useMemo(() => {
          const values: ValueWithEntityReference<PodTimetablesDataReadDTO>[] = [];

          // Aggiungi i dati dei POD normali
          allPodTimetablesData.forEach(data => {
            values.push({
              value: data,
              entityId: data.pod_timetable_id ?? '',
              date: data.datetime ? new Date(data.datetime) : new Date(),
              valueId: data.id ?? undefined,
            });
          });

          // Aggiungi i dati totali se disponibili
          if (shouldLoadTotal && totalPodTimetablesData.length > 0) {
            totalPodTimetablesData.forEach(data => {
              values.push({
                value: data,
                entityId: '__TOTAL__',
                date: data.datetime ? new Date(data.datetime) : new Date(),
                valueId: undefined, // I totali non hanno ID perché sono calcolati
              });
            });
          }

          return values;
        }, [allPodTimetablesData, totalPodTimetablesData, shouldLoadTotal]);

      // Callback quando una cella viene modificata
      const handleCellEditStop = () => {
        // Cell edit stopped
      };

      // Header personalizzato per la prima colonna con icona stagione, giorno settimana, data/ora
      const dateColumnHeader = React.useMemo(() => {
        return (
          <Stack spacing={0.5} sx={{ fontSize: '0.75rem' }}>
            <Typography variant="caption" fontWeight={600}>
              Data e ora
            </Typography>
          </Stack>
        );
      }, []);

      // Leggenda per la colonna delle date quando l'header è espanso
      const dateColumnHeaderExpanded = React.useMemo(() => {
        return (
          <Stack spacing={0.5} sx={{ fontSize: '0.75rem' }}>
            <Typography variant="caption" fontWeight={600} sx={{ pt: '0.2rem' }}>
              Informazioni POD
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              Unità di misura
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              Codice
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              Gerarchia
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              Area funzionale
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              Uso energetico
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              Raggruppamento dati
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              Grafico
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              Note
            </Typography>
            <Box sx={{ height: '24px' }}></Box>
          </Stack>
        );
      }, []);

      // Header personalizzato per le colonne POD (collapsed state)
      const renderPodHeader = React.useCallback(
        (
          pod: PodTimetablesReadDTO,
          hourlyValues: { month: Date; value: number | string | null }[]
        ) => {
          const isTotal = pod.id === '__TOTAL__';
          return (
            <Box sx={{ my: '4px' }}>
              <Typography
                variant="caption"
                fontWeight={600}
                noWrap
                title={isTotal ? 'Totale' : pod.code}
                sx={{ textAlign: 'center' }}
              >
                {isTotal ? 'Totale' : pod.code || '-'}{' '}
                {!isTotal && pod.unit_of_measurement && `[${pod.unit_of_measurement}]`}
              </Typography>
            </Box>
          );
        },
        []
      );

      // Header personalizzato per le colonne POD (expanded state)
      const renderPodHeaderExpanded = React.useCallback(
        (
          pod: PodTimetablesReadDTO,
          hourlyValues: { month: Date; value: number | string | null }[]
        ) => {
          const isTotal = pod.id === '__TOTAL__';
          return (
            <Stack spacing={0.5} sx={{ fontSize: '0.7rem', alignItems: 'center' }}>
              {!isTotal && (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Box sx={{ cursor: 'pointer' }} onClick={() => handleEditClick(pod)}>
                    <EditIcon fontSize="small" />
                  </Box>
                  <Box sx={{ cursor: 'pointer' }} onClick={() => handleDeleteClick(pod.id as any)}>
                    <DeleteIcon fontSize="small" />
                  </Box>
                </Box>
              )}
              {isTotal && (
                <Typography variant="caption" fontWeight={600} noWrap>
                  Totale
                </Typography>
              )}
              {!isTotal && (
                <>
                  <Typography variant="caption" fontWeight={400} noWrap>
                    {pod.unit_of_measurement || '-'}
                  </Typography>
                  <Typography variant="caption" fontWeight={400} noWrap title={pod.code}>
                    {pod.code || '-'}
                  </Typography>
                  <Typography variant="caption" fontWeight={400} noWrap>
                    {pod.hierarchy || '-'}
                  </Typography>
                  <Typography variant="caption" fontWeight={400} noWrap>
                    {pod.functional_area ? getFunctionalAreaLabel(pod.functional_area) : '-'}
                  </Typography>
                  <Typography variant="caption" fontWeight={400} noWrap>
                    {(() => {
                      const energyUseId = (pod as any).energy_use_id;
                      if (!energyUseId) return '-';
                      const energyUse = energyUses.find(eu => eu.id === energyUseId);
                      return energyUse?.code || '-';
                    })()}
                  </Typography>
                  <Typography variant="caption" fontWeight={400} noWrap>
                    {pod.data_grouping || '-'}
                  </Typography>
                  <Typography variant="caption" fontWeight={400} noWrap>
                    {pod.chart || '-'}
                  </Typography>
                  <Typography variant="caption" fontWeight={400} noWrap title={pod.notes}>
                    {pod.notes || '-'}
                  </Typography>
                </>
              )}
            </Stack>
          );
        },
        []
      );

      // Stato per la data di navigazione (deve essere prima dei return condizionali)
      // Inizializza con la data corrente (primo giorno del periodo)
      const [navigationDate, setNavigationDate] = useState<dayjs.Dayjs | null>(() => {
        if (period.from) {
          return dayjs(period.from);
        }
        return null;
      });

      if (!diagnosisId) {
        return (
          <Typography color="text.secondary">
            Salva le informazioni principali della diagnosi per gestire i POD orari.
          </Typography>
        );
      }

      if (!diagnosis?.diagnosis_date) {
        return <Box sx={{ mt: 2 }}>Selezionare una data di diagnosi</Box>;
      }

      if (!period.from || !period.to) {
        return <Box sx={{ mt: 2 }}>Impossibile calcolare il periodo</Box>;
      }

      return (
        <>
          <Stack spacing={2} sx={{ p: 2 }}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
            >
              <Typography variant="h6">POD Orari</Typography>
              {diagnosisSite && (
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  {diagnosisSite.climatic_zone && (
                    <Typography variant="body2" color="text.secondary">
                      Zona climatica: <strong>{diagnosisSite.climatic_zone}</strong>
                    </Typography>
                  )}
                  {diagnosisSite.start_summer && (
                    <Typography variant="body2" color="text.secondary">
                      Estate: <strong>{diagnosisSite.start_summer}</strong>
                    </Typography>
                  )}
                  {diagnosisSite.start_winter && (
                    <Typography variant="body2" color="text.secondary">
                      Inverno: <strong>{diagnosisSite.start_winter}</strong>
                    </Typography>
                  )}
                </Stack>
              )}
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                size="small"
                color="primary"
              >
                <ToggleButton value="table" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TableRowsIcon fontSize="small" />
                  <Typography variant="body2">Tabella</Typography>
                </ToggleButton>
                <ToggleButton value="charts" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <ShowChartIcon fontSize="small" />
                  <Typography variant="body2">Grafici</Typography>
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            <Box sx={{ mt: 3, display: viewMode === 'table' ? 'block' : 'none' }}>
              <CrossEntityDateCrudDataGrid
                ref={crossEntityGridRef}
                granularity="hour"
                periodFrom={period.from}
                periodTo={period.to}
                entities={entitiesWithTotal}
                values={crossEntityValues}
                getEntityLabel={pod => {
                  if (pod.id === '__TOTAL__') {
                    return 'Totale';
                  }
                  return getPodLabel(pod, energyUses);
                }}
                getEntitySortKey={pod => {
                  if (pod.id === '__TOTAL__') {
                    return -9999; // Posiziona "Totale" all'inizio
                  }
                  return (pod as any).index ?? 9999; // NULL values go last
                }}
                getValue={valueObj => valueObj.value ?? null}
                setValue={(valueObj, newValue) => {
                  // Non permettere modifiche alla colonna "Totale"
                  if (valueObj.entityId === '__TOTAL__') {
                    return valueObj; // Ritorna invariato
                  }
                  const updated = { ...valueObj };
                  updated.value =
                    newValue !== null && newValue !== '' ? Number(newValue) : undefined;
                  return updated as any;
                }}
                dateColumnLabel="Data e ora"
                dateColumnHeader={dateColumnHeader}
                dateColumnHeaderExpanded={dateColumnHeaderExpanded}
                renderDateCell={({ date }) => {
                  if (!date) return '';

                  const year = date.getFullYear();
                  const startSummer = diagnosisSite?.start_summer;
                  const startWinter = diagnosisSite?.start_winter;
                  const season = getSeason(date, startSummer, startWinter, year);
                  const isSummer = season === 'summer';

                  const dayOfWeek = dayjs(date).format('ddd');
                  const dateTimeStr = dayjs(date).format('DD/MM/YYYY HH:mm');

                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isSummer ? (
                        <WbSunnyIcon sx={{ fontSize: '1.2rem', color: '#FFA500' }} />
                      ) : (
                        <AcUnitIcon sx={{ fontSize: '1.2rem', color: '#87CEEB' }} />
                      )}
                      <Typography variant="body2">
                        {dayOfWeek}, {dateTimeStr}
                      </Typography>
                    </Box>
                  );
                }}
                renderEntityHeader={renderPodHeader}
                renderEntityHeaderExpanded={renderPodHeaderExpanded}
                entityColumnWidth={120}
                entityColumnWidthExpanded={200}
                onCellEditStop={handleCellEditStop}
                readOnly={readOnly}
                height="calc(100vh - 350px)"
                loading={
                  isLoading ||
                  batchSaveMutation.isLoading ||
                  batchSavePodTimetablesData.isLoading ||
                  updateOrderMutation.isLoading
                }
                enablePaste={true}
                pageSize={24}
                dateNavigation={{
                  value: navigationDate,
                  onChange: newValue => setNavigationDate(newValue),
                }}
                renderCustomFooter={() => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <DatePicker
                      label="Vai alla data"
                      value={navigationDate}
                      onChange={newValue => {
                        if (newValue) {
                          setNavigationDate(newValue);
                          // Naviga alla data selezionata
                          if (crossEntityGridRef.current) {
                            crossEntityGridRef.current.goToDate(newValue.toDate());
                          }
                        }
                      }}
                      format="DD/MM/YYYY"
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: { width: 180 },
                        },
                      }}
                    />
                  </Box>
                )}
              />
            </Box>

            <Box sx={{ mt: 3, display: viewMode === 'charts' ? 'block' : 'none' }}>
              {podTimetables.length === 0 ? (
                <Typography color="text.secondary">
                  Aggiungi almeno un POD per visualizzare i grafici aggregati.
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {(chartsLoading || chartsFetching || totalWeeklyProfileQuery.isLoading) && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={18} />
                      <Typography variant="body2" color="text.secondary">
                        Caricamento profili orari...
                      </Typography>
                    </Stack>
                  )}

                  {/* Grafici del Totale - per primi */}
                  {shouldLoadTotal && totalWeeklyProfiles && (
                    <Box
                      sx={{
                        border: theme => `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        p: 2,
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={600}>
                        Totale
                      </Typography>
                      <Divider sx={{ my: 1.5 }} />
                      <Stack spacing={1.5}>
                        {CHART_SEASONS.map(season => (
                          <Accordion
                            key={`total-${season.key}`}
                            defaultExpanded={season.key === 'all'}
                            disableGutters
                            elevation={0}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                {season.icon}
                                <Typography variant="subtitle2">{season.label}</Typography>
                              </Stack>
                            </AccordionSummary>
                            <AccordionDetails>
                              {totalWeeklyProfiles[season.key]?.length > 0 ? (
                                <WeeklyProfileChart
                                  series={totalWeeklyProfiles[season.key]}
                                  unitOfMeasurement={podTimetables[0]?.unit_of_measurement || ''}
                                />
                              ) : (
                                <Typography color="text.secondary">
                                  Nessun dato disponibile.
                                </Typography>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {/* Grafici dei singoli POD */}
                  {podTimetables.map(pod => {
                    const profiles = pod.id ? weeklyProfilesMap.get(pod.id) : undefined;
                    return (
                      <Box
                        key={pod.id || pod.code}
                        sx={{
                          border: theme => `1px solid ${theme.palette.divider}`,
                          borderRadius: 2,
                          p: 2,
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight={600}>
                          {getPodLabel(pod, energyUses)}
                        </Typography>
                        {pod.notes && (
                          <Typography variant="body2" color="text.secondary">
                            {pod.notes}
                          </Typography>
                        )}
                        <Divider sx={{ my: 1.5 }} />
                        <Stack spacing={1.5}>
                          {CHART_SEASONS.map(season => (
                            <Accordion
                              key={`${pod.id}-${season.key}`}
                              defaultExpanded={season.key === 'all'}
                              disableGutters
                              elevation={0}
                            >
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  {season.icon}
                                  <Typography variant="subtitle2">{season.label}</Typography>
                                </Stack>
                              </AccordionSummary>
                              <AccordionDetails>
                                {profiles ? (
                                  <WeeklyProfileChart
                                    series={profiles[season.key]}
                                    unitOfMeasurement={pod.unit_of_measurement}
                                  />
                                ) : (
                                  <Typography color="text.secondary">
                                    {chartsFetching
                                      ? 'Aggiornamento in corso...'
                                      : 'Nessun dato disponibile.'}
                                  </Typography>
                                )}
                              </AccordionDetails>
                            </Accordion>
                          ))}
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>
            <ActionSpacer></ActionSpacer>
          </Stack>

          <ConfirmDialog
            open={deleteDialogState.open}
            onCancel={handleCloseDeleteDialog}
            onConfirm={handleConfirmDelete}
            title="Conferma eliminazione"
            content="Sei sicuro di voler eliminare questo POD orario?"
            confirmText="Elimina"
            cancelText="Annulla"
            loading={deleteMutation.isLoading}
          />

          <EditPodTimetableDialog
            open={editDialogOpen}
            onClose={handleCloseEditDialog}
            podTimetable={selectedPodTimetable}
            diagnosisId={diagnosisId!}
            existingPodTimetables={podTimetables}
          />

          <MissingDataDialog
            open={missingDataDialogState.open}
            onClose={() =>
              setMissingDataDialogState({
                open: false,
                missingData: [],
                tableData: [],
              })
            }
            onProceed={async () => {
              // Procedi con il salvataggio anche se ci sono date mancanti
              if (missingDataDialogState.tableData.length > 0) {
                await batchSaveMutation.mutateAsync(missingDataDialogState.tableData);
              }
            }}
            missingData={missingDataDialogState.missingData}
          />
        </>
      );
    }
  )
);

DiagnosisPodTimetablesStep.displayName = 'DiagnosisPodTimetablesStep';

interface WeeklyProfileChartProps {
  series?: WeeklySeriesDTO[];
  unitOfMeasurement?: string;
}

const WeeklyProfileChart: React.FC<WeeklyProfileChartProps> = ({ series, unitOfMeasurement }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const hours = useMemo(() => Array.from({ length: 24 }, (_, hour) => hour), []);
  const [lineSeries, setLineSeries] = useState<
    { label: string; data: number[]; color: string; showMark: boolean; area: boolean }[]
  >([]);

  useEffect(() => {
    if (!series || series.length === 0) {
      setLineSeries([]);
      return;
    }

    const computedSeries = series.map((serie, index) => {
      return {
        label: getItalianWeekdayLabel(serie.weekday),
        data: serie.points as any,
        color: WEEKDAY_COLORS[index % WEEKDAY_COLORS.length],
        showMark: false,
        area: false,
      };
    });

    setLineSeries(computedSeries);
  }, [series, hours]);

  if (!lineSeries.length) {
    return <Typography color="text.secondary">Nessun dato disponibile.</Typography>;
  }

  const yAxisLabel = unitOfMeasurement || 'Valore';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isSmallScreen ? 'column' : 'row',
        gap: 2,
        alignItems: isSmallScreen ? 'stretch' : 'flex-start',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: isSmallScreen ? 'column' : 'row',
          alignItems: 'center',
          gap: isSmallScreen ? 0 : 1,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            transform: 'rotate(-90deg)',
            transformOrigin: 'left top',
            whiteSpace: 'nowrap',
          }}
        >
          {yAxisLabel}
        </Typography>
        <LineChartWrapper
          width={isSmallScreen ? 540 : 780}
          height={isSmallScreen ? 300 : 360}
          series={lineSeries}
          xAxis={[
            {
              data: hours,
              valueFormatter: (value: number | null) =>
                value !== null && value !== undefined ? `${value}:00` : '',
              min: 0,
              max: 23,
            },
          ]}
          yAxis={[
            {
              label: '',
              valueFormatter: (value: number | null) =>
                value !== null && value !== undefined
                  ? formatNumberItalian(value, {
                      maximumFractionDigits: DECIMAL_PLACES.grafici_pod_dati,
                    })
                  : '',
            },
          ]}
          margin={{ top: 60, right: 20, left: 60, bottom: 32 }}
          slotProps={{
            legend: {
              direction: 'row',
              position: { vertical: 'top', horizontal: 'right' },
            },
          }}
        />
      </Box>
    </Box>
  );
};
